import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from '@/hooks/use-toast';
import {
  Upload,
  FileText,
  File,
  Eye,
  PenLine,
  Calendar,
  User,
  FolderOpen,
  Shield,
  Video,
  ShieldCheck,
  Clock,
} from 'lucide-react';
import {
  documentService,
  DocumentReference,
} from '@/services/documentService';
import {
  consentService,
  PatientConsentSignature,
} from '@/services/consentService';
import DocumentUploadDialog from '@/components/documents/DocumentUploadDialog';
import { useClinic } from '@/contexts/ClinicContext';
import { useAuth } from '@/contexts/AuthContext';

const CONSENT_TYPE_ICONS: Record<string, React.ReactNode> = {
  telehealth: <Video className='h-4 w-4' />,
  hipaa: <Shield className='h-4 w-4' />,
  financial: <FileText className='h-4 w-4' />,
  treatment: <FileText className='h-4 w-4' />,
  custom: <FileText className='h-4 w-4' />,
};

const CONSENT_TYPE_COLORS: Record<string, string> = {
  telehealth: 'bg-blue-100 text-blue-800 border-blue-200',
  hipaa: 'bg-purple-100 text-purple-800 border-purple-200',
  financial: 'bg-green-100 text-green-800 border-green-200',
  treatment: 'bg-orange-100 text-orange-800 border-orange-200',
  custom: 'bg-gray-100 text-gray-800 border-gray-200',
};

interface PatientDocumentsTabProps {
  patientId?: string;
}

const PatientDocumentsTab: React.FC<PatientDocumentsTabProps> = ({ patientId }) => {
  const [documents, setDocuments] = useState<DocumentReference[]>([]);
  const [consents, setConsents] = useState<PatientConsentSignature[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [signingId, setSigningId] = useState<string | null>(null);
  const [viewingConsent, setViewingConsent] = useState<PatientConsentSignature | null>(null);

  const { clinic, isPractitioner, isAdmin } = useClinic();
  const { user } = useAuth();

  const fetchDocuments = async () => {
    if (!patientId || !clinic?.id) return;
    try {
      setLoading(true);
      const [docsRes, consentsRes] = await Promise.all([
        documentService.getPatientDocuments(clinic.id, patientId),
        consentService.getPatientConsents(clinic.id, patientId).catch((err) => {
          console.error('Error fetching consents:', err);
          return { success: false, data: [] as PatientConsentSignature[] };
        }),
      ]);
      setDocuments(docsRes.documents || []);
      setConsents(consentsRes.success ? (consentsRes.data || []) : []);
    } catch (error) {
      console.error('Error fetching documents:', error);
      toast({
        title: 'Error',
        description: 'Failed to load documents',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDocuments();
  }, [patientId, clinic?.id]);

  const handleView = async (doc: DocumentReference) => {
    try {
      if (!doc.contentUrl) {
        toast({ title: 'Error', description: 'No file attached to this document', variant: 'destructive' });
        return;
      }
      const viewUrl = await documentService.getDocumentViewUrl(doc.contentUrl);
      window.open(viewUrl, '_blank');
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to get document URL', variant: 'destructive' });
    }
  };

  const handleSign = async (documentId: string) => {
    if (!clinic?.id) return;
    try {
      setSigningId(documentId);
      await documentService.signDocument(clinic.id, documentId, user?.practitionerId);
      toast({ title: 'Success', description: 'Document signed', variant: 'success' });
      await fetchDocuments();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to sign document', variant: 'destructive' });
    } finally {
      setSigningId(null);
    }
  };

  const getFileIcon = (contentType?: string) => {
    if (contentType === 'application/pdf') {
      return <FileText className="h-5 w-5 text-red-500" />;
    }
    if (contentType?.startsWith('image/')) {
      return <File className="h-5 w-5 text-blue-500" />;
    }
    return <FileText className="h-5 w-5 text-gray-500" />;
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <Skeleton className="h-7 w-32" />
          <Skeleton className="h-9 w-40" />
        </div>
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-20 w-full rounded-lg" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">Documents</h2>
          <p className="text-sm text-slate-500">
            {documents.length} document{documents.length !== 1 ? 's' : ''}
            {' '}&middot;{' '}
            {consents.length} consent{consents.length !== 1 ? 's' : ''} on file
          </p>
        </div>
        <Button
          onClick={() => setUploadDialogOpen(true)}
          className="bg-[#00A79D] hover:bg-[#008c85]"
          size="sm"
        >
          <Upload className="h-4 w-4 mr-2" />
          Upload Document
        </Button>
      </div>

      {/* Consent forms section */}
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <ShieldCheck className="h-4 w-4 text-slate-500" />
          <h3 className="text-sm font-semibold text-slate-700">Consent Forms</h3>
        </div>
        {consents.length === 0 ? (
          <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 px-4 py-6 text-center">
            <p className="text-sm text-slate-500">No consent forms on file.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {consents.map((consent) => {
              const formType = consent.consentTemplate?.form_type || 'custom';
              const typeColor = CONSENT_TYPE_COLORS[formType] || CONSENT_TYPE_COLORS.custom;
              const typeIcon = CONSENT_TYPE_ICONS[formType] || CONSENT_TYPE_ICONS.custom;
              return (
                <div
                  key={consent.id}
                  className="flex items-center gap-4 rounded-lg border border-slate-200 bg-white p-4 hover:border-slate-300 transition-colors"
                >
                  <div className={`flex-shrink-0 rounded p-1.5 ${typeColor}`}>
                    {typeIcon}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium text-slate-900 truncate">
                        {consent.consentTemplate?.name || 'Consent Form'}
                      </span>
                      <Badge variant="outline" className={`text-xs flex-shrink-0 ${typeColor}`}>
                        {formType}
                      </Badge>
                      <Badge
                        variant="default"
                        className="bg-emerald-100 text-emerald-700 border-emerald-200 text-xs flex-shrink-0"
                      >
                        Signed
                      </Badge>
                    </div>
                    <div className="flex items-center gap-4 mt-1 text-xs text-slate-500 flex-wrap">
                      <span className="flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {formatDate(consent.signed_at)}
                      </span>
                      {consent.expires_at && (
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          Expires {formatDate(consent.expires_at)}
                        </span>
                      )}
                      <span className="text-slate-400">v{consent.template_version}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setViewingConsent(consent)}
                    >
                      <Eye className="h-4 w-4 mr-1" />
                      View
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Clinical documents section */}
      <div className="flex items-center gap-2 pt-2">
        <FolderOpen className="h-4 w-4 text-slate-500" />
        <h3 className="text-sm font-semibold text-slate-700">Clinical Documents</h3>
      </div>
      {documents.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-slate-300 bg-slate-50 py-12">
          <FolderOpen className="h-10 w-10 text-slate-400 mb-3" />
          <p className="text-sm font-medium text-slate-600">No documents uploaded yet</p>
          <p className="text-xs text-slate-400 mt-1">
            Upload PDFs, images, or other clinical documents
          </p>
          <Button
            variant="outline"
            size="sm"
            className="mt-4"
            onClick={() => setUploadDialogOpen(true)}
          >
            <Upload className="h-4 w-4 mr-2" />
            Upload First Document
          </Button>
        </div>
      ) : (
        <div className="space-y-2">
          {documents.map((doc) => (
            <div
              key={doc.id}
              className="flex items-center gap-4 rounded-lg border border-slate-200 bg-white p-4 hover:border-slate-300 transition-colors"
            >
              <div className="flex-shrink-0">{getFileIcon(doc.contentType)}</div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-slate-900 truncate">
                    {doc.title}
                  </span>
                  {doc.type && (
                    <Badge variant="secondary" className="text-xs flex-shrink-0">
                      {doc.type}
                    </Badge>
                  )}
                  <Badge
                    variant={doc.signed ? 'default' : 'outline'}
                    className={
                      doc.signed
                        ? 'bg-emerald-100 text-emerald-700 border-emerald-200 text-xs flex-shrink-0'
                        : 'text-amber-600 border-amber-300 text-xs flex-shrink-0'
                    }
                  >
                    {doc.signed ? 'Signed' : 'Preliminary'}
                  </Badge>
                </div>
                <div className="flex items-center gap-4 mt-1 text-xs text-slate-500">
                  <span className="flex items-center gap-1">
                    <Calendar className="h-3 w-3" />
                    {formatDate(doc.date)}
                  </span>
                  {doc.author && (
                    <span className="flex items-center gap-1">
                      <User className="h-3 w-3" />
                      {doc.author}
                    </span>
                  )}
                  {doc.signed && doc.signedBy && (
                    <span className="flex items-center gap-1">
                      <PenLine className="h-3 w-3" />
                      Signed by {doc.signedBy}
                    </span>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-2 flex-shrink-0">
                <Button variant="ghost" size="sm" onClick={() => handleView(doc)}>
                  <Eye className="h-4 w-4 mr-1" />
                  View
                </Button>
                {isPractitioner && !doc.signed && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleSign(doc.id)}
                    disabled={signingId === doc.id}
                  >
                    <PenLine className="h-4 w-4 mr-1" />
                    {signingId === doc.id ? 'Signing...' : 'Sign'}
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Upload dialog */}
      {patientId && clinic?.id && (
        <DocumentUploadDialog
          open={uploadDialogOpen}
          onOpenChange={setUploadDialogOpen}
          patientId={patientId}
          clinicId={clinic.id}
          onUploadComplete={fetchDocuments}
        />
      )}

      {/* Consent view dialog */}
      <Dialog
        open={!!viewingConsent}
        onOpenChange={(open) => !open && setViewingConsent(null)}
      >
        <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
          {viewingConsent && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  {CONSENT_TYPE_ICONS[viewingConsent.consentTemplate?.form_type || 'custom']}
                  {viewingConsent.consentTemplate?.name || 'Consent Form'}
                </DialogTitle>
                <DialogDescription>
                  Signed on {formatDate(viewingConsent.signed_at)}
                  {' '}&middot; v{viewingConsent.template_version}
                </DialogDescription>
              </DialogHeader>

              <div
                className="prose prose-sm dark:prose-invert max-w-none rounded-lg border bg-muted/30 p-4 [&>h2]:mb-2 [&>h2]:mt-4 [&>h2]:text-lg [&>h2]:font-semibold [&>h3]:mb-2 [&>h3]:mt-3 [&>h3]:text-base [&>h3]:font-medium [&>li]:mb-1 [&>ol]:mb-3 [&>ol]:list-decimal [&>ol]:pl-6 [&>ol]:text-sm [&>p]:mb-3 [&>p]:text-sm [&>ul]:mb-3 [&>ul]:list-disc [&>ul]:pl-6 [&>ul]:text-sm"
                dangerouslySetInnerHTML={{ __html: viewingConsent.signed_content }}
              />

              <div className="rounded-lg border bg-slate-50 p-4">
                <h4 className="text-sm font-semibold text-slate-700 mb-3">Signature</h4>
                {viewingConsent.signature_type === 'drawn' && viewingConsent.signature_data?.data ? (
                  <img
                    src={viewingConsent.signature_data.data}
                    alt="Signature"
                    className="max-h-32 bg-white rounded border border-slate-200"
                  />
                ) : viewingConsent.signature_type === 'typed' && viewingConsent.signature_data?.data ? (
                  <p className="text-2xl italic text-slate-900" style={{ fontFamily: 'cursive' }}>
                    {viewingConsent.signature_data.data}
                  </p>
                ) : (
                  <p className="text-sm text-slate-600">
                    Acknowledged on {formatDate(viewingConsent.signed_at)}
                  </p>
                )}
                <p className="mt-3 text-xs text-slate-500">
                  Signed at: {new Date(viewingConsent.signed_at).toLocaleString()}
                </p>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PatientDocumentsTab;
